import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import RoleBasedHeader from '../../components/ui/RoleBasedHeader';
import DashboardNavigation from '../../components/ui/DashboardNavigation';

import QuickActionPanel from '../../components/ui/QuickActionPanel';
import KPICard from './components/KPICard';
import MembershipGrowthChart from './components/MembershipGrowthChart';
import RevenueBreakdownChart from './components/RevenueBreakdownChart';
import ClassUtilizationChart from './components/ClassUtilizationChart';
import MemberSegmentationTable from './components/MemberSegmentationTable';
import TrainerProductivityCard from './components/TrainerProductivityCard';
import AtRiskMembersAlert from './components/AtRiskMembersAlert';
import FinancialSummaryCard from './components/FinancialSummaryCard';
import DateRangeFilter from './components/DateRangeFilter';
import OpenAIAnalyticsInsights from './components/OpenAIAnalyticsInsights';

const AnalyticsDashboard = () => {
  const { userProfile } = useAuth();
  const userRole = userProfile?.role || 'manager';
  const userName = userProfile?.full_name || 'User';

  const kpiData = [
  {
    title: 'Active Members',
    value: '1,247',
    change: '+12.5%',
    changeType: 'positive',
    icon: 'Users',
    iconColor: 'var(--color-primary)',
    trend: 'up'
  },
  {
    title: 'Monthly Recurring Revenue',
    value: '$87,450',
    change: '+8.3%',
    changeType: 'positive',
    icon: 'DollarSign',
    iconColor: 'var(--color-success)',
    trend: 'up'
  },
  {
    title: 'Class Utilization',
    value: '78%',
    change: '+5.2%',
    changeType: 'positive',
    icon: 'Activity',
    iconColor: 'var(--color-accent)',
    trend: 'up'
  },
  {
    title: 'Churn Risk',
    value: '4.2%',
    change: '-1.8%',
    changeType: 'positive',
    icon: 'AlertTriangle',
    iconColor: 'var(--color-warning)',
    trend: 'down'
  }];


  const membershipGrowthData = [
  { month: 'Jul', active: 1050, new: 85, churned: 32 },
  { month: 'Aug', active: 1103, new: 92, churned: 39 },
  { month: 'Sep', active: 1156, new: 78, churned: 25 },
  { month: 'Oct', active: 1209, new: 95, churned: 42 },
  { month: 'Nov', active: 1262, new: 88, churned: 35 },
  { month: 'Dec', active: 1315, new: 102, churned: 49 },
  { month: 'Jan', active: 1247, new: 67, churned: 135 }];


  const revenueBreakdownData = [
  { service: 'Memberships', revenue: 52470 },
  { service: 'Personal Training', revenue: 18900 },
  { service: 'Class Bookings', revenue: 12350 },
  { service: 'Retail', revenue: 3730 }];


  const classUtilizationData = [
  { name: 'HIIT', value: 85 },
  { name: 'Yoga', value: 72 },
  { name: 'Spin', value: 68 },
  { name: 'CrossFit', value: 91 },
  { name: 'Pilates', value: 64 }];


  const memberSegmentationData = [
  {
    name: 'Premium Members',
    count: 342,
    avgRevenue: 149,
    retention: 94,
    retentionTrend: 'up',
    engagement: 87,
    color: 'var(--color-primary)'
  },
  {
    name: 'Standard Members',
    count: 658,
    avgRevenue: 79,
    retention: 86,
    retentionTrend: 'up',
    engagement: 72,
    color: 'var(--color-success)'
  },
  {
    name: 'Basic Members',
    count: 247,
    avgRevenue: 49,
    retention: 78,
    retentionTrend: 'down',
    engagement: 58,
    color: 'var(--color-accent)'
  },
  {
    name: 'Trial Members',
    count: 89,
    avgRevenue: 0,
    retention: 45,
    retentionTrend: 'down',
    engagement: 34,
    color: 'var(--color-warning)'
  }];


  const topTrainers = [
  {
    name: 'Marcus Thompson',
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_183f5ab63-1763296652710.png",
    avatarAlt: 'Professional headshot of African American male trainer with short black hair wearing navy blue athletic shirt',
    specialty: 'Strength & Conditioning',
    clients: 47,
    revenue: '12,450'
  },
  {
    name: 'Emily Chen',
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_15780cdc0-1763299445992.png",
    avatarAlt: 'Professional headshot of Asian female trainer with long black hair in ponytail wearing teal athletic top',
    specialty: 'Yoga & Flexibility',
    clients: 52,
    revenue: '11,890'
  },
  {
    name: 'David Rodriguez',
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_16d452239-1763299445724.png",
    avatarAlt: 'Professional headshot of Hispanic male trainer with short brown hair wearing red athletic shirt',
    specialty: 'HIIT & Cardio',
    clients: 39,
    revenue: '9,730'
  },
  {
    name: 'Jessica Williams',
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_114ddb8bf-1763298867995.png",
    avatarAlt: 'Professional headshot of Caucasian female trainer with blonde hair in bun wearing purple athletic top',
    specialty: 'CrossFit',
    clients: 41,
    revenue: '10,250'
  }];


  const atRiskMembers = [
  {
    name: 'John Anderson',
    reason: 'No check-ins in 14 days',
    riskScore: 87
  },
  {
    name: 'Maria Garcia',
    reason: 'Cancelled 3 classes this month',
    riskScore: 72
  },
  {
    name: 'Robert Kim',
    reason: 'Membership expires in 5 days',
    riskScore: 68
  },
  {
    name: 'Lisa Brown',
    reason: 'Decreased visit frequency',
    riskScore: 65
  },
  {
    name: 'Michael Davis',
    reason: 'Failed payment attempt',
    riskScore: 91
  }];


  const financialData = {
    totalRevenue: 87450,
    revenueGrowth: 8.3,
    membershipFees: 52470,
    personalTraining: 18900,
    classBookings: 12350,
    retail: 3730,
    outstanding: 4280,
    failedPayments: 12
  };

  const handleQuickAction = (actionId) => {
    console.log('Quick action triggered:', actionId);
  };

  const handleDateRangeChange = (range) => {
    console.log('Date range changed:', range);
  };

  return (
    <div className="min-h-screen bg-background">
      <RoleBasedHeader userRole={userRole} userName={userName} />
      <div className="page-content-wrapper">
        <DashboardNavigation userRole={userRole} />
      </div>
      <main className="container mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8 lg:py-12">
        <div className="mb-6 md:mb-8">
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-semibold text-foreground mb-3">
            Analytics Dashboard
          </h1>
          <p className="text-base md:text-lg text-muted-foreground">
            Comprehensive business intelligence and performance metrics
          </p>
        </div>

        <div className="mb-6 md:mb-8">
          <DateRangeFilter onFilterChange={handleDateRangeChange} />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-6 md:mb-8">
          {kpiData?.map((kpi, index) =>
          <KPICard key={index} {...kpi} />
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6 mb-6 md:mb-8">
          <MembershipGrowthChart data={membershipGrowthData} />
          <RevenueBreakdownChart data={revenueBreakdownData} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6 mb-6 md:mb-8">
          <div className="lg:col-span-2">
            <ClassUtilizationChart data={classUtilizationData} />
          </div>
          <FinancialSummaryCard data={financialData} />
        </div>

        <div className="mb-6 md:mb-8">
          <MemberSegmentationTable data={memberSegmentationData} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
          <TrainerProductivityCard trainers={topTrainers} />
          <AtRiskMembersAlert members={atRiskMembers} />
        </div>

        {/* OpenAI Analytics Insights - New Section */}
        <div className="mb-6">
          <OpenAIAnalyticsInsights
            analyticsData={{
              kpis: kpiData,
              membershipGrowth: membershipGrowthData,
              revenue: revenueBreakdownData,
              classUtilization: classUtilizationData,
              atRiskMembers: atRiskMembers
            }} />

        </div>
      </main>
      <QuickActionPanel userRole={userRole} onAction={handleQuickAction} />
    </div>);

};

export default AnalyticsDashboard;